<script lang="ts">
  import { onMount, tick, untrack } from 'svelte';
  import Icon from '../ui/Icon.svelte';
  import {
    addDays,
    addMonths,
    addMonthsToDate,
    calendarMonth,
    periodLength,
    periodPresets,
    shiftPeriod,
    type PeriodMode,
    type PeriodRange,
  } from './period';

  let {
    mode,
    from,
    to,
    min,
    max,
    variant = 'bar',
    onChange,
  }: {
    mode: PeriodMode;
    from: string | null;
    to: string | null;
    min: string;
    max: string;
    variant?: 'bar' | 'sheet';
    onChange: (from: string, to: string) => void;
  } = $props();

  const pickerId = `period-picker-${crypto.randomUUID()}`;
  const monthNames = [
    'Gennaio',
    'Febbraio',
    'Marzo',
    'Aprile',
    'Maggio',
    'Giugno',
    'Luglio',
    'Agosto',
    'Settembre',
    'Ottobre',
    'Novembre',
    'Dicembre',
  ];
  const weekDays = ['L', 'M', 'M', 'G', 'V', 'S', 'D'];
  const fullDateFormatter = new Intl.DateTimeFormat('it-IT', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const compactDateFormatter = new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  });
  const monthFormatter = new Intl.DateTimeFormat('it-IT', {
    month: 'short',
    year: 'numeric',
  });

  let rootElement: HTMLDivElement;
  let triggerElement: HTMLButtonElement;
  let open = $state(false);
  let draftFrom = $state(untrack(() => from ?? min));
  let draftTo = $state(untrack(() => to ?? max));
  let selectionAnchor = $state<string | null>(null);
  let hoveredValue = $state<string | null>(null);
  let cursorMonth = $state(untrack(() => (to ?? max).slice(0, 7)));
  let cursorYear = $state(untrack(() => Number((to ?? max).slice(0, 4))));

  let presets = $derived(periodPresets(mode, min, max));
  let activePresetId = $derived(
    presets.find((preset) => preset.from === draftFrom && preset.to === draftTo)?.id ?? null,
  );
  let duration = $derived(periodLength(mode, { from: draftFrom, to: draftTo }));
  let filtered = $derived(draftFrom !== min || draftTo !== max);
  let rangeLabel = $derived(`${formatValue(draftFrom, true)} – ${formatValue(draftTo, true)}`);
  let durationLabel = $derived(
    mode === 'month'
      ? `${duration} ${duration === 1 ? 'mese' : 'mesi'}`
      : `${duration} ${duration === 1 ? 'giorno' : 'giorni'}`,
  );
  let availableYears = $derived(
    Array.from(
      { length: Number(max.slice(0, 4)) - Number(min.slice(0, 4)) + 1 },
      (_, index) => Number(min.slice(0, 4)) + index,
    ),
  );

  $effect(() => {
    if (open) return;
    draftFrom = from ?? min;
    draftTo = to ?? max;
  });

  onMount(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (open && rootElement && !rootElement.contains(event.target as Node)) closePicker();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (open && event.key === 'Escape') {
        event.preventDefault();
        closePicker(true);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  });

  function dateFromValue(value: string): Date {
    return new Date(`${mode === 'month' ? `${value}-01` : value}T12:00:00`);
  }

  function formatValue(value: string, compact = false): string {
    if (mode === 'month') return monthFormatter.format(dateFromValue(value));
    return (compact ? compactDateFormatter : fullDateFormatter).format(dateFromValue(value));
  }

  function openPicker(): void {
    draftFrom = from ?? min;
    draftTo = to ?? max;
    selectionAnchor = null;
    hoveredValue = null;
    cursorMonth = draftTo.slice(0, 7);
    cursorYear = Number(draftTo.slice(0, 4));
    open = true;
  }

  function closePicker(returnFocus = false): void {
    open = false;
    selectionAnchor = null;
    hoveredValue = null;
    if (returnFocus) void tick().then(() => triggerElement?.focus());
  }

  function togglePicker(): void {
    if (open) closePicker();
    else openPicker();
  }

  function commitRange(range: PeriodRange): void {
    draftFrom = range.from;
    draftTo = range.to;
    selectionAnchor = null;
    hoveredValue = null;
    cursorMonth = range.to.slice(0, 7);
    cursorYear = Number(range.to.slice(0, 4));
    onChange(range.from, range.to);
  }

  function selectValue(value: string): void {
    if (value < min || value > max) return;
    if (!selectionAnchor) {
      selectionAnchor = value;
      draftFrom = value;
      draftTo = value;
      hoveredValue = null;
      return;
    }
    commitRange(
      value < selectionAnchor
        ? { from: value, to: selectionAnchor }
        : { from: selectionAnchor, to: value },
    );
  }

  function selectSingleValue(value: string): void {
    if (value >= min && value <= max) commitRange({ from: value, to: value });
  }

  function previewRange(): PeriodRange {
    if (!selectionAnchor || !hoveredValue) return { from: draftFrom, to: draftTo };
    return hoveredValue < selectionAnchor
      ? { from: hoveredValue, to: selectionAnchor }
      : { from: selectionAnchor, to: hoveredValue };
  }

  function isInPreviewRange(value: string): boolean {
    const preview = previewRange();
    return value >= preview.from && value <= preview.to;
  }

  function shift(direction: -1 | 1): void {
    commitRange(shiftPeriod(mode, { from: draftFrom, to: draftTo }, direction, min, max));
  }

  function changeCursorMonth(month: number): void {
    cursorMonth = `${cursorMonth.slice(0, 4)}-${String(month).padStart(2, '0')}`;
  }

  function changeCursorYear(year: number): void {
    cursorMonth = `${year}-${cursorMonth.slice(5)}`;
  }

  async function focusCalendarValue(value: string): Promise<void> {
    await tick();
    let target = Array.from(
      rootElement.querySelectorAll<HTMLButtonElement>(`[data-period-value="${value}"]`),
    ).find((element) => element.getClientRects().length > 0);
    if (!target && mode === 'date') {
      cursorMonth = value.slice(0, 7);
      await tick();
      target = Array.from(
        rootElement.querySelectorAll<HTMLButtonElement>(`[data-period-value="${value}"]`),
      ).find((element) => element.getClientRects().length > 0);
    }
    target?.focus();
  }

  function onDayKeydown(event: KeyboardEvent, value: string): void {
    const dayOffsets: Record<string, number> = {
      ArrowLeft: -1,
      ArrowRight: 1,
      ArrowUp: -7,
      ArrowDown: 7,
    };
    let target: string | null = null;
    if (event.key in dayOffsets) target = addDays(value, dayOffsets[event.key]!);
    else if (event.key === 'PageUp') target = addMonthsToDate(value, -1);
    else if (event.key === 'PageDown') target = addMonthsToDate(value, 1);
    if (!target) return;
    if (target < min || target > max) return;
    event.preventDefault();
    void focusCalendarValue(target);
  }

  function isFocusValue(value: string, calendarValue: string): boolean {
    if (draftTo.startsWith(calendarValue)) return value === draftTo;
    if (draftFrom.startsWith(calendarValue)) return value === draftFrom;
    const firstAvailable = calendarMonth(calendarValue).days.find(
      (day) => day.value >= min && day.value <= max,
    );
    return value === firstAvailable?.value;
  }
</script>

<div
  bind:this={rootElement}
  class:period-picker--sheet={variant === 'sheet'}
  class:period-picker--open={open}
  class="period-picker period-picker--{variant}"
>
  <button
    bind:this={triggerElement}
    class="period-trigger"
    class:is-filtered={filtered}
    type="button"
    aria-expanded={open}
    aria-controls={pickerId}
    aria-label={`Periodo: ${rangeLabel}`}
    data-period-min={min}
    data-period-max={max}
    onclick={togglePicker}
  >
    <span class="period-trigger-icon" aria-hidden="true"><Icon name="calendar" size={18} /></span>
    <span class="period-trigger-copy">
      <small>Periodo</small>
      <strong>{rangeLabel}</strong>
    </span>
    <span class="period-trigger-duration">{durationLabel}</span>
    <span class="period-trigger-chevron" aria-hidden="true"
      ><Icon name="chevron-down" size={16} /></span
    >
  </button>

  {#if open}
    <button
      class="period-picker-backdrop"
      type="button"
      aria-label="Chiudi selettore periodo"
      onclick={() => closePicker()}
    ></button>
    <div
      class="period-picker-popover"
      id={pickerId}
      role={variant === 'sheet' ? 'group' : 'dialog'}
      aria-modal={variant === 'bar' ? 'false' : undefined}
      aria-labelledby={`${pickerId}-title`}
    >
      <header class="period-picker-header">
        <div>
          <p class="eyebrow">Intervallo temporale</p>
          <h3 id={`${pickerId}-title`}>Seleziona periodo</h3>
          <p>
            {selectionAnchor ? 'Ora scegli la data finale' : `${rangeLabel} · ${durationLabel}`}
          </p>
        </div>
        <button
          class="icon-button"
          type="button"
          aria-label="Chiudi selettore periodo"
          onclick={() => closePicker(true)}><Icon name="close" size={18} /></button
        >
      </header>

      <div class="period-picker-body">
        <aside class="period-shortcuts" aria-label="Scorciatoie periodo">
          <div class="period-section-heading">
            <span>Scorciatoie</span><small>Relative all’ultimo dato</small>
          </div>
          <div class="period-preset-list">
            {#each presets as preset (preset.id)}
              <button
                class:active={activePresetId === preset.id}
                type="button"
                aria-pressed={activePresetId === preset.id}
                onclick={() => commitRange(preset)}
              >
                <span>{preset.label}</span><small>{preset.description}</small>
              </button>
            {/each}
          </div>
          <div class="period-shift-actions">
            <button
              type="button"
              disabled={draftFrom <= min}
              onclick={() => shift(-1)}
              title="Sposta indietro mantenendo la stessa durata"
              ><Icon name="chevron-left" size={15} /> Precedente</button
            >
            <button
              type="button"
              disabled={draftTo >= max}
              onclick={() => shift(1)}
              title="Sposta avanti mantenendo la stessa durata"
              >Successivo <Icon name="chevron-right" size={15} /></button
            >
          </div>
        </aside>

        <section class="period-custom-range" aria-label="Intervallo personalizzato">
          <div class="period-selection" aria-live="polite">
            <div class:pending={selectionAnchor !== null}>
              <span>Inizio</span><strong>{formatValue(draftFrom)}</strong>
            </div>
            <span aria-hidden="true">→</span>
            <div>
              <span>Fine</span><strong>{selectionAnchor ? 'Scegli…' : formatValue(draftTo)}</strong>
            </div>
          </div>

          {#if mode === 'date'}
            <div class="period-calendar-toolbar">
              <button
                type="button"
                aria-label="Mese precedente"
                disabled={cursorMonth <= min.slice(0, 7)}
                onclick={() => (cursorMonth = addMonths(cursorMonth, -1))}
                ><Icon name="chevron-left" size={17} /></button
              >
              <div>
                <label>
                  <span class="sr-only">Mese visualizzato</span>
                  <select
                    value={Number(cursorMonth.slice(5))}
                    onchange={(event) => changeCursorMonth(Number(event.currentTarget.value))}
                  >
                    {#each monthNames as month, index}
                      <option value={index + 1}>{month}</option>
                    {/each}
                  </select>
                </label>
                <label>
                  <span class="sr-only">Anno visualizzato</span>
                  <select
                    value={Number(cursorMonth.slice(0, 4))}
                    onchange={(event) => changeCursorYear(Number(event.currentTarget.value))}
                  >
                    {#each availableYears as year}<option value={year}>{year}</option>{/each}
                  </select>
                </label>
              </div>
              <button
                type="button"
                aria-label="Mese successivo"
                disabled={cursorMonth >= max.slice(0, 7)}
                onclick={() => (cursorMonth = addMonths(cursorMonth, 1))}
                ><Icon name="chevron-right" size={17} /></button
              >
            </div>

            <div class="period-calendar-grid">
              {#each [addMonths(cursorMonth, -1), cursorMonth] as calendarValue (calendarValue)}
                {@const calendar = calendarMonth(calendarValue)}
                <div class="period-calendar" aria-label={formatValue(`${calendarValue}-01`)}>
                  <strong
                    >{monthNames[Number(calendarValue.slice(5)) - 1]}
                    {calendarValue.slice(0, 4)}</strong
                  >
                  <div class="period-weekdays" aria-hidden="true">
                    {#each weekDays as day}<span>{day}</span>{/each}
                  </div>
                  <div class="period-days">
                    {#each Array(calendar.leadingEmptyDays) as _}<span aria-hidden="true"
                      ></span>{/each}
                    {#each calendar.days as day (day.value)}
                      <button
                        class:in-range={isInPreviewRange(day.value)}
                        class:range-start={day.value === previewRange().from}
                        class:range-end={day.value === previewRange().to}
                        class:today={day.value === max}
                        type="button"
                        disabled={day.value < min || day.value > max}
                        tabindex={isFocusValue(day.value, calendarValue) ? 0 : -1}
                        aria-label={formatValue(day.value)}
                        aria-pressed={day.value === draftFrom || day.value === draftTo}
                        data-period-value={day.value}
                        onmouseenter={() => selectionAnchor && (hoveredValue = day.value)}
                        onmouseleave={() => (hoveredValue = null)}
                        onclick={() => selectValue(day.value)}
                        ondblclick={() => selectSingleValue(day.value)}
                        onkeydown={(event) => onDayKeydown(event, day.value)}>{day.day}</button
                      >
                    {/each}
                  </div>
                </div>
              {/each}
            </div>
          {:else}
            <div class="period-calendar-toolbar period-year-toolbar">
              <button
                type="button"
                aria-label="Anno precedente"
                disabled={cursorYear <= Number(min.slice(0, 4))}
                onclick={() => (cursorYear -= 1)}><Icon name="chevron-left" size={17} /></button
              >
              <label>
                <span class="sr-only">Anno visualizzato</span>
                <select
                  value={cursorYear}
                  onchange={(event) => (cursorYear = Number(event.currentTarget.value))}
                >
                  {#each availableYears as year}<option value={year}>{year}</option>{/each}
                </select>
              </label>
              <button
                type="button"
                aria-label="Anno successivo"
                disabled={cursorYear >= Number(max.slice(0, 4))}
                onclick={() => (cursorYear += 1)}><Icon name="chevron-right" size={17} /></button
              >
            </div>
            <div class="period-month-grid">
              {#each monthNames as month, index}
                {@const value = `${cursorYear}-${String(index + 1).padStart(2, '0')}`}
                <button
                  class:in-range={isInPreviewRange(value)}
                  class:range-start={value === previewRange().from}
                  class:range-end={value === previewRange().to}
                  class:today={value === max}
                  type="button"
                  disabled={value < min || value > max}
                  aria-pressed={value === draftFrom || value === draftTo}
                  data-period-value={value}
                  onmouseenter={() => selectionAnchor && (hoveredValue = value)}
                  onmouseleave={() => (hoveredValue = null)}
                  onclick={() => selectValue(value)}
                  ondblclick={() => selectSingleValue(value)}
                >
                  <span>{month.slice(0, 3)}</span><small>{cursorYear}</small>
                </button>
              {/each}
            </div>
          {/if}

          <p class="period-picker-hint">
            {selectionAnchor
              ? 'Secondo click: completa l’intervallo.'
              : 'Click: scegli inizio e fine · Doppio click: singolo periodo'}
          </p>
        </section>
      </div>

      <footer class="period-picker-footer">
        <span>Dati disponibili: {formatValue(min, true)} – {formatValue(max, true)}</span>
        <button type="button" onclick={() => closePicker(true)}>Fatto</button>
      </footer>
    </div>
  {/if}
</div>
